/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import ErpGdlCarteiras from '../../api/erpGdlCarteiras';
import {
  downloadCsvFile,
  generateFileName,
  downloadBinaryFile,
} from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      assignments_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      assignments_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    assignments_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
  viewMode: 'general',
  customers: {
    uiFlags: { isFetching: false },
    data: [],
  },
  summaries: {
    agent: [],
    team: [],
    inbox: [],
  },
  templates: {
    uiFlags: { isFetching: false },
    summary: {
      total: 0,
      sent: 0,
      failed: 0,
      success_rate: 0,
    },
    byTemplate: [],
    byError: [],
    timeseries: [],
    records: [],
    meta: {
      total_count: 0,
      current_page: 1,
      per_page: 25,
      total_pages: 0,
    },
  },
  whatsappCosts: {
    uiFlags: { isFetching: false },
    summary: {},
    byCategory: [],
    byInbox: [],
    timeseries: [],
    local: {},
    warnings: [],
    period: {},
  },
  ctwa: {
    uiFlags: { isFetching: false, isDownloading: false },
    summary: {
      total_conversations: 0,
      total_attributions: 0,
      resolved: 0,
      open: 0,
    },
    byCampaign: [],
    timeseries: [],
    records: [],
    meta: {
      total_count: 0,
      current_page: 1,
      per_page: 25,
      total_pages: 0,
    },
  },
  gdlCarteiras: {
    uiFlags: { isFetching: false },
    summary: {
      total: 0,
      sent: 0,
      skipped: 0,
      failed: 0,
      success_rate: 0,
    },
    byCarteira: [],
    byTemplate: [],
    byAtrasoFaixa: [],
    byStatus: [],
    bySkipMotivo: [],
    timeseries: [],
    records: [],
    funnel: {
      data: [],
      meta: { total_count: 0 },
    },
    incompleteData: {
      summary: { total: 0 },
      byCarteira: [],
      byMissingField: [],
      records: [],
      meta: {
        total_count: 0,
        current_page: 1,
        per_page: 25,
        total_pages: 0,
      },
    },
    meta: {
      total_count: 0,
      current_page: 1,
      per_page: 25,
      total_pages: 0,
    },
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getCustomers(_state) {
    return _state.customers.data;
  },
  getCustomersUIFlags(_state) {
    return _state.customers.uiFlags;
  },
  getTemplateReports(_state) {
    return _state.templates;
  },
  getTemplateReportsUIFlags(_state) {
    return _state.templates.uiFlags;
  },
  getWhatsappCostReports(_state) {
    return _state.whatsappCosts;
  },
  getWhatsappCostReportsUIFlags(_state) {
    return _state.whatsappCosts.uiFlags;
  },
  getCtwaReports(_state) {
    return _state.ctwa;
  },
  getCtwaReportsUIFlags(_state) {
    return _state.ctwa.uiFlags;
  },
  getGdlCarteirasReports(_state) {
    return _state.gdlCarteiras;
  },
  getGdlCarteirasReportsUIFlags(_state) {
    return _state.gdlCarteiras.uiFlags;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj)
      .then(accountReport => {
        let { data } = accountReport;
        data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
        commit(types.default.SET_ACCOUNT_REPORTS, {
          metric,
          data,
        });
      })
      .catch(error => {
        // Evita spinner infinito em caso de falha
        console.error('[REPORTS] fetchAccountReport failed', metric, error);
      })
      .finally(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
          metric,
          value: false,
        });
      });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours,
      reportObj.label_ids,
      reportObj.label_match
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, reportObj.page)
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    // Force excel for label reports
    const payload = { ...reportObj, exportFormat: 'xlsx' };
    return Report.getLabelReports(payload)
      .then(response => {
        downloadBinaryFile(
          payload.fileName,
          response.data,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: payload?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchCustomers({ commit }, reportObj) {
    commit(types.default.SET_CUSTOMERS_UI_FLAGS, { isFetching: true });
    return Report.getCustomers(reportObj)
      .then(resp => {
        const payload = resp.data?.payload || [];
        commit(types.default.SET_CUSTOMERS, payload);
      })
      .finally(() =>
        commit(types.default.SET_CUSTOMERS_UI_FLAGS, { isFetching: false })
      );
  },
  fetchTemplateReports({ commit }, reportObj) {
    commit(types.default.SET_TEMPLATE_REPORTS_UI_FLAGS, { isFetching: true });
    return Report.getTemplateReports(reportObj)
      .then(resp => {
        commit(types.default.SET_TEMPLATE_REPORTS, resp.data);
      })
      .finally(() =>
        commit(types.default.SET_TEMPLATE_REPORTS_UI_FLAGS, {
          isFetching: false,
        })
      );
  },
  fetchWhatsappCostReports({ commit }, reportObj) {
    commit(types.default.SET_WHATSAPP_COST_REPORTS_UI_FLAGS, {
      isFetching: true,
    });
    return Report.getWhatsappCostReports(reportObj)
      .then(resp => {
        commit(types.default.SET_WHATSAPP_COST_REPORTS, resp.data);
      })
      .finally(() =>
        commit(types.default.SET_WHATSAPP_COST_REPORTS_UI_FLAGS, {
          isFetching: false,
        })
      );
  },
  fetchCtwaReports({ commit }, reportObj) {
    commit(types.default.SET_CTWA_REPORTS_UI_FLAGS, { isFetching: true });
    return Report.getCtwaReports(reportObj)
      .then(resp => {
        commit(types.default.SET_CTWA_REPORTS, resp.data);
      })
      .finally(() =>
        commit(types.default.SET_CTWA_REPORTS_UI_FLAGS, { isFetching: false })
      );
  },
  downloadCtwaReports({ commit }, reportObj) {
    const exportFormat = reportObj.exportFormat || 'csv';
    const isExcel = exportFormat === 'xlsx' || exportFormat === 'excel';
    commit(types.default.SET_CTWA_REPORTS_UI_FLAGS, { isDownloading: true });

    return Report.getCtwaReports({ ...reportObj, exportFormat })
      .then(response => {
        if (isExcel) {
          downloadBinaryFile(
            reportObj.fileName,
            response.data,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
        }
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'ctwa',
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() =>
        commit(types.default.SET_CTWA_REPORTS_UI_FLAGS, {
          isDownloading: false,
        })
      );
  },
  fetchGdlCarteirasReports({ commit }, reportObj) {
    commit(types.default.SET_GDL_CARTEIRAS_REPORTS_UI_FLAGS, {
      isFetching: true,
    });
    return ErpGdlCarteiras.report(reportObj)
      .then(resp => {
        commit(types.default.SET_GDL_CARTEIRAS_REPORTS, resp.data);
      })
      .finally(() =>
        commit(types.default.SET_GDL_CARTEIRAS_REPORTS_UI_FLAGS, {
          isFetching: false,
        })
      );
  },
  fetchGdlCarteirasFunnel({ commit }, reportObj) {
    return ErpGdlCarteiras.funnel(reportObj).then(resp => {
      commit(types.default.SET_GDL_CARTEIRAS_FUNNEL, resp.data);
    });
  },
  fetchGdlCarteirasIncompleteData({ commit }, reportObj) {
    return ErpGdlCarteiras.incompleteData(reportObj).then(resp => {
      commit(types.default.SET_GDL_CARTEIRAS_INCOMPLETE_DATA, resp.data);
    });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_CUSTOMERS](_state, data) {
    _state.customers.data = data;
  },
  [types.default.SET_CUSTOMERS_UI_FLAGS](_state, flags) {
    _state.customers.uiFlags = { ..._state.customers.uiFlags, ...flags };
  },
  [types.default.SET_TEMPLATE_REPORTS](_state, data) {
    _state.templates = {
      ..._state.templates,
      summary: data.summary || _state.templates.summary,
      byTemplate: data.by_template || [],
      byError: data.by_error || [],
      timeseries: data.timeseries || [],
      records: data.records || [],
      meta: data.meta || _state.templates.meta,
    };
  },
  [types.default.SET_TEMPLATE_REPORTS_UI_FLAGS](_state, flags) {
    _state.templates.uiFlags = { ..._state.templates.uiFlags, ...flags };
  },
  [types.default.SET_WHATSAPP_COST_REPORTS](_state, data) {
    _state.whatsappCosts = {
      ..._state.whatsappCosts,
      summary: data.summary || {},
      byCategory: data.by_category || [],
      byInbox: data.by_inbox || [],
      timeseries: data.timeseries || [],
      local: data.local || {},
      warnings: data.warnings || [],
      period: data.period || {},
    };
  },
  [types.default.SET_WHATSAPP_COST_REPORTS_UI_FLAGS](_state, flags) {
    _state.whatsappCosts.uiFlags = {
      ..._state.whatsappCosts.uiFlags,
      ...flags,
    };
  },
  [types.default.SET_CTWA_REPORTS](_state, data) {
    _state.ctwa = {
      ..._state.ctwa,
      summary: data.summary || _state.ctwa.summary,
      byCampaign: data.by_campaign || [],
      timeseries: data.timeseries || [],
      records: data.records || [],
      meta: data.meta || _state.ctwa.meta,
    };
  },
  [types.default.SET_CTWA_REPORTS_UI_FLAGS](_state, flags) {
    _state.ctwa.uiFlags = { ..._state.ctwa.uiFlags, ...flags };
  },
  [types.default.SET_GDL_CARTEIRAS_REPORTS](_state, data) {
    _state.gdlCarteiras = {
      ..._state.gdlCarteiras,
      summary: data.summary || _state.gdlCarteiras.summary,
      byCarteira: data.by_carteira || [],
      byTemplate: data.by_template || [],
      byAtrasoFaixa: data.by_atraso_faixa || [],
      byStatus: data.by_status || [],
      bySkipMotivo: data.by_skip_motivo || [],
      timeseries: data.timeseries || [],
      records: data.records || [],
      meta: data.meta || _state.gdlCarteiras.meta,
    };
  },
  [types.default.SET_GDL_CARTEIRAS_FUNNEL](_state, data) {
    _state.gdlCarteiras.funnel = {
      data: data.data || [],
      meta: data.meta || { total_count: 0 },
    };
  },
  [types.default.SET_GDL_CARTEIRAS_INCOMPLETE_DATA](_state, data) {
    _state.gdlCarteiras.incompleteData = {
      summary: data.summary || { total: 0 },
      byCarteira: data.by_carteira || [],
      byMissingField: data.by_missing_field || [],
      records: data.records || [],
      meta: data.meta || {
        total_count: 0,
        current_page: 1,
        per_page: 25,
        total_pages: 0,
      },
    };
  },
  [types.default.SET_GDL_CARTEIRAS_REPORTS_UI_FLAGS](_state, flags) {
    _state.gdlCarteiras.uiFlags = {
      ..._state.gdlCarteiras.uiFlags,
      ...flags,
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
